import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useBreadcrumbs } from '../layout/breadcrumbsContext/useBreadcrumbs/useBreadcrumbs'

import { Flex, Tab, Tabs } from '@percent/lemonade'
import { SettingsTabs } from './Settings.types'
import { Security } from './security/Security'
import styles from './Settings.module.scss'
import { useAuthState } from '@percent/partner-dashboard/common/hooks'
import { selectScopes } from '@percent/partner-dashboard/context/auth'
import { PartnerScopes } from '@percent/partner-dashboard/constants/scopes'
import { ScimContainer } from './scim/ScimContainer'
import { KeyList } from './key/KeyList'
import { UsersContainer } from './users/UsersContainer'
import { usePermissionCheck } from '@percent/partner-dashboard/common/hooks/useAllow/usePermissionCheck'

export function Settings() {
  const { t } = useTranslation()
  const { partner } = useAuthState()
  const { userCan } = usePermissionCheck()
  const canViewApiKeys = userCan('viewApiKeys')
  const canViewUsers = userCan('viewUsers')
  const scopes = selectScopes(partner?.scopes)
  const hasScimApiScope = scopes[PartnerScopes.ScimApi]

  const [settingsTab, setSettingsTab] = useState<SettingsTabs>(SettingsTabs.SECURITY)

  const tabs: { label: string; key: SettingsTabs }[] = [
    {
      label: t('typography.security'),
      key: SettingsTabs.SECURITY
    },
    ...(canViewUsers
      ? [
          {
            label: t('typography.users'),
            key: SettingsTabs.USERS
          }
        ]
      : []),
    ...(hasScimApiScope
      ? [
          {
            label: t('typography.scimProvisioning'),
            key: SettingsTabs.SCIM_PROVISIONING
          }
        ]
      : []),
    ...(canViewApiKeys ? [{ label: t('typography.apiKeys'), key: SettingsTabs.API_KEYS }] : [])
  ]

  useBreadcrumbs([
    {
      label: t('breadcrumb.settings')
    }
  ])

  return (
    <Flex direction="column" bg="white" className={styles.settingsContainer}>
      <Tabs
        items={tabs}
        activeTab={settingsTab}
        onChangeTab={newTab => {
          setSettingsTab(newTab as SettingsTabs)
        }}
      />
      <Tab data-testid="tab-security" index={SettingsTabs.SECURITY} value={settingsTab}>
        <Security />
      </Tab>
      {canViewUsers && (
        <Tab data-testid="tab-users" index={SettingsTabs.USERS} value={settingsTab}>
          <UsersContainer />
        </Tab>
      )}
      {hasScimApiScope && (
        <Tab data-testid="tab-scim-provisioning" index={SettingsTabs.SCIM_PROVISIONING} value={settingsTab}>
          <ScimContainer />
        </Tab>
      )}
      {canViewApiKeys && (
        <Tab data-testid="tab-api-keys" index={SettingsTabs.API_KEYS} value={settingsTab}>
          <KeyList />
        </Tab>
      )}
    </Flex>
  )
}
